import { DatePipe } from '@angular/common';
import { CdkConnectedOverlay, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, QueryList, ViewChild, ViewChildren, inject, signal} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MensagemModel } from '../../../shared/model/chat/mensagens/mensagem-model';
import { MethodsUtils } from '../../../shared/util/methods.utils';
import { InfoArquivo } from '../../models/info-arquivo.model';
import { EXTENSOES_AUDIO, EXTENSOES_DOWNLOAD, EXTENSOES_IMAGEM, EXTENSOES_VIDEO } from '../../utils/chat.constantes';
import { animacaoPop, animacaoSurgir } from '../../utils/chat-animacoes.utils';
import { MenuReacaoComponent } from './menu-reacao/menu-reacao.component';
import { ModoMenuReacao } from './menu-reacao/models/modo-menu-reacao.model';
import { ModalInfoMensagemComponent } from './modal-info-mensagem/modal-info-mensagem.component';
import { PlayerAudioComponent } from './player-audio/player-audio.component';
import { StatusMensagemComponent } from '../../status-mensagem/status-mensagem.component';
import { ReacaoBalao } from './models/reacao-balao.model';
import { ARRASTE_MAXIMO, CORTE_TEXTO, ICONE_BOTAO_TEMPLATE, ICONE_BOTAO_TEMPLATE_PADRAO, LIMIAR_ARRASTE_RESPOSTA, LIMITE_TEXTO, TEMPO_TOQUE_LONGO } from './utils/balao-mensagem.constantes';

@Component({
  selector: 'app-balao-mensagem',
  standalone: true,
  imports: [
    DatePipe,
    MatIcon,
    MatTooltip,
    OverlayModule,
    PlayerAudioComponent,
    MenuReacaoComponent,
    StatusMensagemComponent
  ],
  templateUrl: './balao-mensagem.component.html',
  styleUrl: './balao-mensagem.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [animacaoPop, animacaoSurgir]
})
export class BalaoMensagemComponent implements AfterViewInit, OnDestroy {

  @Input() mensagem: MensagemModel = new MensagemModel('');
  @Input() contato = '';
  @Input() isTemplatePreview = false;
  @Input() podeResponder = false;
  @Input() nomeContato = '';
  @Input() fotoContato = '';
  @Output() reenviarMensagem = new EventEmitter<MensagemModel>();
  @Output() responder = new EventEmitter<MensagemModel>();
  @Output() abrirMidia = new EventEmitter<MensagemModel>();
  @Output() reagir = new EventEmitter<ReacaoBalao>();
  @Output() irParaResposta = new EventEmitter<MensagemModel>();
  @Output() audioTerminou = new EventEmitter<MensagemModel>();

  menuReacao = signal<ModoMenuReacao | null>(null);

  readonly posicoesMenu: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 }
  ];

  readonly posicoesDetalhes: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 }
  ];

  @ViewChildren(CdkConnectedOverlay) private overlaysReacao?: QueryList<CdkConnectedOverlay>;
  @ViewChild(PlayerAudioComponent) private playerAudio?: PlayerAudioComponent;

  arraste = signal(0);
  imagemComErro = signal(false);
  textoExpandido = signal(false);

  private inicioArraste: { x: number; y: number; id: number } | null = null;
  private arrasteHorizontal = false;
  private temporizadorToqueLongo: ReturnType<typeof setTimeout> | null = null;
  private tocouLongo = false;
  private textoCortadoCache: { texto: string; html: string } | null = null;

  private readonly elementRef = inject(ElementRef);
  private readonly dialog = inject(MatDialog);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly zone = inject(NgZone);
  private removerPonteiro?: () => void;
  private readonly safeUrlCache = new Map<string, SafeResourceUrl>();

  ngAfterViewInit(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    this.zone.runOutsideAngular(() => {
      const pressionar = (e: PointerEvent) => this.aoPressionarLinha(e);
      const mover = (e: PointerEvent) => this.aoMoverLinha(e);
      const soltar = () => this.aoSoltarLinha();
      const perdeuCaptura = (e: Event) => { if (e.target === host) this.aoSoltarLinha(); };
      const menuContexto = (e: Event) => { if (this.tocouLongo) e.preventDefault(); };
      const clique = (e: Event) => {
        if (!this.tocouLongo) return;
        e.stopPropagation();
        e.preventDefault();
        this.tocouLongo = false;
      };
      host.addEventListener('pointerdown', pressionar, { passive: true });
      host.addEventListener('pointermove', mover, { passive: true });
      host.addEventListener('pointerup', soltar, { passive: true });
      host.addEventListener('pointercancel', soltar, { passive: true });
      host.addEventListener('lostpointercapture', perdeuCaptura, { passive: true });
      host.addEventListener('contextmenu', menuContexto);
      host.addEventListener('click', clique, true);
      this.removerPonteiro = () => {
        host.removeEventListener('pointerdown', pressionar);
        host.removeEventListener('pointermove', mover);
        host.removeEventListener('pointerup', soltar);
        host.removeEventListener('pointercancel', soltar);
        host.removeEventListener('lostpointercapture', perdeuCaptura);
        host.removeEventListener('contextmenu', menuContexto);
        host.removeEventListener('click', clique, true);
      };
    });
  }

  ngOnDestroy(): void {
    this.removerPonteiro?.();
    this.cancelarToqueLongo();
  }

  tocarAudio(): boolean {
    if (!this.playerAudio) return false;
    this.playerAudio.tocar();
    (this.elementRef.nativeElement as HTMLElement).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return true;
  }

  clicarResponder(): void {
    this.responder.emit(this.mensagem);
  }

  get podeReagir(): boolean {
    return this.podeResponder && this.ehCliente && !!this.mensagem?.metaId && !this.isTemplatePreview;
  }

  abrirMenuReacao(modo: ModoMenuReacao, event?: Event): void {
    event?.stopPropagation();
    this.menuReacao.set(this.menuReacao() === modo ? null : modo);
  }

  reposicionarMenuReacao(): void {
    this.overlaysReacao?.forEach(overlay => {
      if (overlay.open) overlay.overlayRef?.updatePosition();
    });
  }

  fecharMenuReacao(): void {
    this.menuReacao.set(null);
  }

  escolherReacao(emoji: string): void {
    this.fecharMenuReacao();
    this.reagir.emit({ mensagem: this.mensagem, emoji: this.mensagem.reacao === emoji ? '' : emoji });
  }

  removerReacao(): void {
    this.fecharMenuReacao();
    this.reagir.emit({ mensagem: this.mensagem, emoji: '' });
  }

  clicarResposta(resposta: MensagemModel, event: Event): void {
    event.stopPropagation();
    this.irParaResposta.emit(resposta);
  }

  private aoPressionarLinha(event: PointerEvent): void {
    if (event.pointerType === 'mouse') return;
    this.tocouLongo = false;
    this.inicioArraste = { x: event.clientX, y: event.clientY, id: event.pointerId };
    this.arrasteHorizontal = false;
    if (this.podeReagir) {
      this.cancelarToqueLongo();
      this.temporizadorToqueLongo = setTimeout(() => {
        this.temporizadorToqueLongo = null;
        this.tocouLongo = true;
        this.inicioArraste = null;
        navigator.vibrate?.(15);
        this.zone.run(() => this.menuReacao.set('seletor'));
      }, TEMPO_TOQUE_LONGO);
    }
  }

  private aoMoverLinha(event: PointerEvent): void {
    const inicio = this.inicioArraste;
    if (!inicio || inicio.id !== event.pointerId) return;
    const dx = event.clientX - inicio.x;
    const dy = event.clientY - inicio.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.cancelarToqueLongo();
    if (!this.podeResponder || !this.mensagem?.metaId) return;
    if (!this.arrasteHorizontal) {
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        this.inicioArraste = null;
        return;
      }
      if (dx < 10) return;
      this.arrasteHorizontal = true;
      (this.elementRef.nativeElement as HTMLElement).setPointerCapture?.(event.pointerId);
    }
    const deslocamento = dx <= LIMIAR_ARRASTE_RESPOSTA ? dx : LIMIAR_ARRASTE_RESPOSTA + (dx - LIMIAR_ARRASTE_RESPOSTA) * 0.3;
    const novo = Math.max(0, Math.min(ARRASTE_MAXIMO, deslocamento));
    if (novo !== this.arraste()) this.zone.run(() => this.arraste.set(novo));
  }

  private aoSoltarLinha(): void {
    this.cancelarToqueLongo();
    if (!this.inicioArraste && this.arraste() === 0) return;
    this.zone.run(() => {
      const responder = this.arraste() >= LIMIAR_ARRASTE_RESPOSTA;
      this.inicioArraste = null;
      this.arrasteHorizontal = false;
      this.arraste.set(0);
      if (responder) {
        navigator.vibrate?.(12);
        this.responder.emit(this.mensagem);
      }
    });
  }

  private cancelarToqueLongo(): void {
    if (this.temporizadorToqueLongo) clearTimeout(this.temporizadorToqueLongo);
    this.temporizadorToqueLongo = null;
  }

  get progressoArraste(): number {
    return Math.min(1, this.arraste() / LIMIAR_ARRASTE_RESPOSTA);
  }

  get ehCliente(): boolean {
    return this.mensagem?.sendBy === 'CLIENTE';
  }

  get temArquivo(): boolean {
    const m = this.mensagem;
    return !!(m?.uuidImage?.length || m?.urlImage?.length);
  }

  get ehFigurinha(): boolean {
    return this.temArquivo && MethodsUtils.ehFigurinha(this.mensagem);
  }

  get ehMidia(): boolean {
    const m = this.mensagem;
    return !!m && this.temArquivo && !this.ehFigurinha && (this.verificarImagem(m) || this.verificarVideo(m));
  }

  get temBotoesTemplate(): boolean {
    return !!this.mensagem?.botoes?.length;
  }

  get horaNoTexto(): boolean {
    return !!this.mensagem?.mensagem && !this.mensagem?.opcoes?.length && !this.temBotoesTemplate && !this.mensagem?.mensagemRodape;
  }

  iconeBotaoTemplate(tipo?: string): string {
    return ICONE_BOTAO_TEMPLATE[(tipo ?? '').toUpperCase()] ?? ICONE_BOTAO_TEMPLATE_PADRAO;
  }

  get horaNaLinhaAudio(): boolean {
    const m = this.mensagem;
    return !!m && !m.mensagem && !m.opcoes?.length && this.verificarAudio(m) && !this.ehMidia;
  }

  get horaSobreImagem(): boolean {
    const m = this.mensagem;
    return !!m && !m.mensagem && !m.opcoes?.length && !this.temBotoesTemplate && this.ehMidia && !this.imagemComErro();
  }

  get larguraRodape(): number {
    const temBadge = !this.isTemplatePreview && !!(this.mensagem?.disparoId || this.mensagem?.isTemplate);
    const temStatus = !!this.mensagem?.enviando || this.enviadoPeloSistema;
    return 40 + (temBadge ? 20 : 0) + (temStatus ? 18 : 0);
  }

  get statusEnvio(): string {
    return (this.mensagem?.status ?? '').toString().toUpperCase();
  }

  get enviadoPeloSistema(): boolean {
    return (this.mensagem?.sendBy ?? '').toString().toUpperCase() === 'SISTEMA';
  }

  verificarImagem(mensagem: MensagemModel): boolean {
    return EXTENSOES_IMAGEM.includes(this.extrairExtensao(mensagem));
  }

  verificarVideo(mensagem: MensagemModel): boolean {
    return EXTENSOES_VIDEO.includes(this.extrairExtensao(mensagem));
  }

  verificarAudio(mensagem: MensagemModel): boolean {
    return EXTENSOES_AUDIO.includes(this.extrairExtensao(mensagem));
  }

  verificarPdf(mensagem: MensagemModel): boolean {
    return this.extrairExtensao(mensagem) === 'pdf';
  }

  verificarTipoArquivoDowload(mensagem: MensagemModel): boolean {
    return EXTENSOES_DOWNLOAD.includes(this.extrairExtensao(mensagem));
  }

  get extensao(): string {
    return this.extrairExtensao(this.mensagem);
  }

  get infoArquivo(): InfoArquivo {
    return MethodsUtils.infoArquivo(this.extensao);
  }

  resumoResposta(mensagem: MensagemModel): string {
    return MethodsUtils.resumoMensagem(mensagem);
  }

  get textoLongo(): boolean {
    return (this.mensagem?.mensagem?.length ?? 0) > LIMITE_TEXTO;
  }

  get htmlTexto(): string {
    const m = this.mensagem;
    if (!this.textoLongo || this.textoExpandido()) {
      return m.formatada && m.mensagemFormatada ? m.mensagemFormatada : this.formatarMensagem(m);
    }
    if (this.textoCortadoCache?.texto !== m.mensagem) {
      const bruto = m.mensagem.slice(0, CORTE_TEXTO);
      const corte = bruto.lastIndexOf(' ') > CORTE_TEXTO * 0.7 ? bruto.slice(0, bruto.lastIndexOf(' ')) : bruto;
      this.textoCortadoCache = { texto: m.mensagem, html: MethodsUtils.formatarMensagemComNegritoItalicoRiscada(corte, false) + '…' };
    }
    return this.textoCortadoCache.html;
  }

  expandirTexto(event: Event): void {
    event.stopPropagation();
    this.textoExpandido.set(true);
  }

  formatarMensagem(mensagem: MensagemModel): string {
    const texto = mensagem.mensagem;
    if (!texto) return '';
    const formatado = MethodsUtils.formatarMensagemComNegritoItalicoRiscada(texto, false);
    mensagem.formatada = true;
    mensagem.mensagemFormatada = formatado;
    return formatado;
  }

  getSafeUrl(url: string): SafeResourceUrl {
    let safeUrl = this.safeUrlCache.get(url);
    if (!safeUrl) {
      safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.safeUrlCache.set(url, safeUrl);
    }
    return safeUrl;
  }

  urlCapaVideo(url: string): SafeResourceUrl {
    return this.getSafeUrl(url.includes('#') ? url : `${url}#t=0.1`);
  }

  duracaoVideo = signal('');

  aoCarregarVideo(event: Event): void {
    const duracao = (event.target as HTMLVideoElement).duration;
    if (!Number.isFinite(duracao)) return;
    this.duracaoVideo.set(MethodsUtils.formatarTempoMidia(Math.round(duracao)));
  }

  extrairNomeArquivo(url: string): string {
    return MethodsUtils.nomeDoArquivo(url);
  }

  baixar(event: Event): void {
    event.stopPropagation();
    MethodsUtils.baixarArquivo(this.mensagem.urlImage, this.extrairNomeArquivo(this.mensagem.urlImage));
  }

  enviarNovamenteFunc(mensagem: MensagemModel): void {
    this.reenviarMensagem.emit(mensagem);
  }

  clicarMidia(event: Event): void {
    event.stopPropagation();
    if (!this.imagemComErro()) this.abrirMidia.emit(this.mensagem);
  }

  abrirModalInfoMensagem(tipo: number): void {
    this.dialog.open(ModalInfoMensagemComponent,
      MethodsUtils.configPainelLateralChat({ mensagem: this.mensagem, tipo, contato: this.contato }, '400px'));
  }

  private extrairExtensao(mensagem: MensagemModel): string {
    return MethodsUtils.extensaoDoArquivo(mensagem?.extensao || mensagem?.urlImage || '');
  }
}
